import type { TFunction } from 'i18next';
import type { CartLine } from '@/types/cart';
import { catalogName } from '@/lib/localizeCatalog';

// SC-574: sale_api's order rejects, mapped to customer-facing messages. Extracted
// from CheckoutPage so the shared-table checkout maps the same rejects — both
// pages POST into the same `_handle_dine_in` code path, so a code handled on one
// screen and not the other was purely accidental.
//
// The wire shapes are NOT uniform. Depending on the branch a reject arrives as:
//   { code: "SESSION_EXPIRED" }              dine-in branch
//   { detail: "LICENSE_EXPIRED" }            online branch — SAME enum, other key
//   { detail: "product_unavailable", products: [...] }
//   { delivery: ["OUT_OF_RANGE"] }           delivery quote, no code/detail at all
//   { recaptcha: "captcha_failed" }          captcha, no code/detail at all
//   { customer: { email: ["Enter a…"] } }    DRF field error (DF-0573)
// so every shape is read explicitly; anything unrecognised falls back to generic.

// L9-2: sale_api's redemption-code reject reasons — mapped to loyalty.checkout.errors.*
// and shown inline near the code input, never via the generic toast.
const REDEMPTION_ERROR_REASONS = new Set([
  'invalid_redemption',
  'already_used',
  'expired',
  'not_found',
  'reward_not_applicable',
  'redemption_unavailable',
  'redemption_conflict',
  // SC-1091: the advantage leaves under 0,50 € to pay ONLINE (Stripe's floor);
  // sale_api refuses before creating anything.
  'online_amount_too_small',
]);

// Rejects sale_api spells out under `code` (dine-in branch) or `detail` (online
// branch). Both keys are looked up here so the same restaurant condition reads
// the same on both branches.
const ENUM_MESSAGE_KEYS: Record<string, string> = {
  PENDING_ROUND_EXISTS: 'checkout.errors.pendingRoundExists',
  // Spec 2026-08-28 paid-table-ordering-lock — le convive a poste malgre le
  // bouton grise (ou depuis un onglet ouvert avant le paiement). Cacher un
  // bouton n'a jamais empeche personne de poster : le serveur tranche, et il
  // faut une phrase pour sa reponse.
  SESSION_PAID_LOCKED: 'tablePayment.errorPaidLocked',
  ROUNDS_EXHAUSTED: 'checkout.errors.roundsExhausted',
  SESSION_EXPIRED: 'checkout.errors.sessionExpired',
  SUR_PLACE_DISABLED: 'checkout.errors.surPlaceDisabled',
  LICENSE_EXPIRED: 'checkout.errors.licenseExpired',
  RESOURCE_UNAVAILABLE: 'checkout.errors.serviceUnavailable',
  IDEMPOTENCY_CONFLICT: 'checkout.errors.orderConflict',
  WAITER_REVOKED: 'checkout.errors.waiterRevoked',
  SHARED_CART_DISABLED: 'checkout.errors.sharedCartDisabled',
  EMPTY_CART: 'cart.empty',
};

// Rejects that only ever arrive lowercase under `detail`.
const DETAIL_MESSAGE_KEYS: Record<string, string> = {
  closed: 'checkout.errors.closed',
  restaurant_closed: 'checkout.errors.closed',
  online_ordering_disabled: 'checkout.errors.closed',
  order_mode_disabled: 'checkout.errors.orderModeDisabled',
  scheduled_at_in_past: 'checkout.errors.slotUnavailable',
  scheduled_at_out_of_hours: 'checkout.errors.slotUnavailable',
  scheduled_at_invalid: 'checkout.errors.slotUnavailable',
  total_mismatch: 'checkout.errors.totalMismatch',
  invalid_consumer_token: 'checkout.errors.sessionInvalid',
  consumer_check_unavailable: 'checkout.errors.serviceUnavailable',
};

// Reasons compute_quote() can refuse an address with, surfaced by the order POST
// as { delivery: [reason] } — no `detail`, so they used to stringify to
// "[object Object]" and fall through to the generic message.
const DELIVERY_MESSAGE_KEYS: Record<string, string> = {
  DELIVERY_DISABLED: 'checkout.errors.deliveryDisabledNow',
  OUT_OF_RANGE: 'checkout.errors.deliveryOutOfRange',
  CITY_NOT_SERVED: 'checkout.errors.deliveryCityNotServed',
  GEOCODE_FAILED: 'checkout.errors.deliveryAddressNotFound',
  ROUTES_API_ERROR: 'checkout.errors.deliveryQuoteFailed',
  MISSING_ORIGIN: 'checkout.errors.deliveryNotConfigured',
};

// DF-0573 — top-level keys that carry a named reject above, never a field error.
const NON_FIELD_KEYS = new Set([
  'code', 'detail', 'products', 'minimum', 'seconds_remaining', 'max_items', 'delivery',
  'min_order_ttc', 'recaptcha', 'payment_method', 'quote', 'city', 'sector', 'total_ttc',
  'non_field_errors',
]);

export type ContactField = 'name' | 'phone' | 'email';

const CONTACT_MESSAGE_KEYS: Record<ContactField, string> = {
  name: 'reservation.invalidName',
  phone: 'reservation.invalidPhone',
  email: 'reservation.invalidEmail',
};

// SC-1594 — any other refused field, by its top-level key, to a sentence the
// customer can act on. The raw key never reaches the screen (it goes to the
// diagnostic log only): a key not listed here — restaurant_uuid, language,
// idempotency_key… — is nothing the customer can correct, so it reads
// « rechargez la page ».
const FIELD_MESSAGE_KEYS: Record<string, string> = {
  scheduled_at: 'checkout.errors.fieldSchedule',
  is_asap: 'checkout.errors.fieldSchedule',
  address: 'checkout.errors.fieldAddress',
  delivery_destination: 'checkout.errors.fieldAddress',
  delivery_fee_ttc: 'checkout.errors.fieldAddress',
  lines: 'checkout.errors.fieldCart',
  cart_options: 'checkout.errors.fieldCart',
  course_labels: 'checkout.errors.fieldCart',
  customer_note: 'checkout.errors.fieldNote',
  redemption_code: 'checkout.errors.fieldRedemption',
  payment_channel: 'checkout.errors.fieldPayment',
  type: 'checkout.errors.fieldMode',
  service_choice: 'checkout.errors.fieldMode',
};

const fieldRoot = (path: string) => path.split(/[.[]/)[0];

interface RejectPayload {
  code?: string;
  detail?: unknown;
  products?: unknown;
  minimum?: unknown;
  seconds_remaining?: number;
  max_items?: number;
  delivery?: unknown;
  min_order_ttc?: unknown;
  recaptcha?: unknown;
  payment_method?: unknown;
  quote?: unknown;
}

function rejectData(err: unknown): RejectPayload | undefined {
  const payload = err as { data?: unknown };
  return typeof payload.data === 'object' && payload.data !== null
    ? (payload.data as RejectPayload)
    : undefined;
}

function rejectDetail(err: unknown): string {
  const payload = err as { data?: unknown };
  const data = rejectData(err);
  return data && 'detail' in data ? String(data.detail) : String(payload.data ?? '');
}

// DF-0573 — the paths of the fields a DRF 400 refused, e.g. `customer.email`,
// `lines[0].qty`, `language`. Keys only: the values the customer typed never
// leave this function (they go to the diagnostic log). Empty when the body is
// not a field error.
export function rejectedFieldKeys(err: unknown): string[] {
  const data = rejectData(err);
  if (!data) return [];
  const paths: string[] = [];
  const isMessages = (v: unknown) => Array.isArray(v) && v.length > 0 && v.every((m) => typeof m === 'string');
  const walk = (value: unknown, path: string, depth: number) => {
    if (isMessages(value)) {
      paths.push(path);
    } else if (Array.isArray(value) && depth < 3) {
      value.forEach((item, i) => walk(item, `${path}[${i}]`, depth + 1));
    } else if (value && typeof value === 'object' && depth < 3) {
      Object.entries(value).forEach(([key, item]) => walk(item, `${path}.${key}`, depth + 1));
    }
  };
  for (const [key, value] of Object.entries(data)) {
    if (!NON_FIELD_KEYS.has(key)) walk(value, key, 0);
  }
  return paths.slice(0, 10);
}

// DF-0573 — the contact field sale_api refused, so the checkout can bring the
// customer back to « Vos coordonnées » with that field marked.
export function rejectedContactField(err: unknown): ContactField | null {
  const keys = rejectedFieldKeys(err);
  for (const field of ['email', 'phone', 'name'] as const) {
    if (keys.includes(`customer.${field}`)) return field;
  }
  return null;
}

// Names the cart lines whose product uuid the backend listed as offending, so the
// customer knows what to remove instead of reading a uuid.
function namedProducts(data: RejectPayload | undefined, lines?: CartLine[]): string[] {
  const ids = new Set(Array.isArray(data?.products) ? data.products.map(String) : []);
  return [...new Set(
    (lines ?? []).filter((line) => ids.has(line.itemId)).map((line) => catalogName(line.snapshot)),
  )];
}

// SC-1091: an ONLINE order that a loyalty discount brings below the card minimum
// (0,50 €): `{"detail":"online_amount_too_small","minimum":"0.50","total_ttc":"…"}`.
export function isOnlineAmountTooSmall(err: unknown): boolean {
  const payload = err as { status?: number };
  return payload.status === 400 && rejectDetail(err) === 'online_amount_too_small';
}

// SC-1091: sale_api compared the `total_ttc` we sent to its own total and they
// differ by more than a cent. The screen is now exact (loyalty discount included),
// so this means something moved between display and send — re-read, don't retry blind.
export function isTotalMismatch(err: unknown): boolean {
  const payload = err as { status?: number };
  return payload.status === 400 && rejectDetail(err) === 'total_mismatch';
}

// Returns the mapped i18n message when `err` is a 400 carrying one of the
// redemption reject reasons above, else null (let the generic handling apply).
export function redemptionErrorMessage(err: unknown, t: TFunction): string | null {
  const payload = err as { status?: number };
  if (payload.status !== 400) return null;
  const detail = rejectDetail(err);
  if (!detail || !REDEMPTION_ERROR_REASONS.has(detail)) return null;
  return t(`loyalty.checkout.errors.${detail}`);
}

// SC-316: a slot the backend rejects as expired / out-of-hours / invalid. Used to
// block validation (the customer must pick a new slot) — distinct from generic errors.
export function isSlotExpiredError(err: unknown): boolean {
  const detail = rejectDetail(err);
  return (
    detail === 'scheduled_at_in_past' ||
    detail === 'scheduled_at_out_of_hours' ||
    detail === 'scheduled_at_invalid' ||
    // SC-1594 — the same slot refused as a DRF field error.
    rejectedFieldKeys(err).some((path) => FIELD_MESSAGE_KEYS[fieldRoot(path)] === 'checkout.errors.fieldSchedule')
  );
}

// SC-587: the two rejects that mean "a cart line is no longer orderable", with
// the product uuid(s) the backend named. The checkout turns these into the same
// one-click removal the cart drawer offers, instead of a toast the customer can
// only read. `product_unavailable` carries a single uuid (resource_api's quote
// bails on the first bad line); `product_unavailable_in_service_period` lists
// every offender. An empty array means the reject is not one of those two, or
// named no uuid at all — the caller falls back to the generic message.
export function unavailableProductIds(err: unknown): string[] {
  const data = rejectData(err);
  const detail = rejectDetail(err);
  if (detail !== 'product_unavailable' && detail !== 'product_unavailable_in_service_period') {
    return [];
  }
  return Array.isArray(data?.products) ? data.products.map(String) : [];
}

export function orderErrorMessage(err: unknown, t: TFunction, lines?: CartLine[]): string {
  const payload = err as { status?: number | string };
  // RTK Query reports a lost connection as a string status with no body. "The
  // order failed, try again" sends the customer back into the same dead retry;
  // naming the connection tells them what to fix.
  if (payload.status === 'FETCH_ERROR' || payload.status === 'TIMEOUT_ERROR') {
    return t('checkout.errors.network');
  }
  const data = rejectData(err);
  const detail = rejectDetail(err);

  // Parameterised rejects first — they need the server's own numbers.
  if (data?.code === 'ROUND_COOLDOWN') {
    return t('checkout.errors.roundCooldown', { seconds: data.seconds_remaining ?? 0 });
  }
  if (data?.code === 'DINERS_RATE_LIMIT') {
    return t('checkout.errors.dinersRateLimit', { minutes: Math.ceil((data.seconds_remaining ?? 0) / 60) });
  }
  if (data?.code === 'ROUND_BUDGET_EXCEEDED') {
    return t('checkout.errors.roundBudgetExceeded', { max: data.max_items ?? 0 });
  }

  const enumKey = (data?.code && ENUM_MESSAGE_KEYS[data.code]) || ENUM_MESSAGE_KEYS[detail];
  if (enumKey) return t(enumKey);

  if (detail === 'product_unavailable_in_service_period') {
    // Backend wire-check caught a lunch-only/dinner-only product for the
    // selected slot (stale cart kept across service periods). Name the
    // offending dishes so the customer knows what to remove.
    const names = namedProducts(data, lines);
    return names.length
      ? t('checkout.errors.productUnavailablePeriodNamed', { products: names.join(', ') })
      : t('checkout.errors.productUnavailablePeriod');
  }
  if (detail === 'product_unavailable') {
    // SC-335: a cart product was deactivated/removed after add-to-cart; the
    // backend rejects with the offending product UUID(s). SC-574: when none of
    // them resolves to a cart line (stale cart, renamed product) the fallback
    // used to be the fully generic message — at least say it's a cart problem.
    const names = namedProducts(data, lines);
    return names.length
      ? t('checkout.errors.productUnavailableNamed', { products: names.join(', ') })
      : t('checkout.errors.productUnavailable');
  }
  if (detail === 'order_below_minimum') {
    // SC-554: le seuil a change (ou le cache 30 s de la config resource_api a
    // expire) entre l'affichage du panier et l'envoi. Le garde-fou serveur
    // fait autorite — on nomme le montant qu'il renvoie plutot que celui,
    // potentiellement perime, du panier.
    const minimum = Number(data?.minimum);
    return Number.isFinite(minimum)
      ? t('checkout.errors.orderBelowMinimum', {
          minimum: minimum.toFixed(2).replace('.', ','),
        })
      : t('checkout.errors.generic');
  }

  const detailKey = DETAIL_MESSAGE_KEYS[detail];
  if (detailKey) return t(detailKey);

  // Shapes carrying neither `code` nor `detail`.
  if (data?.recaptcha) return t('checkout.errors.recaptchaFailed');
  if (Array.isArray(data?.delivery)) {
    // BELOW_MIN_ORDER carries the threshold with it, so it can't live in
    // DELIVERY_MESSAGE_KEYS — that map holds fixed messages only.
    if (data.delivery.includes('BELOW_MIN_ORDER')) {
      const min = String(data.min_order_ttc ?? '');
      return t('checkout.errors.belowDeliveryMinimum', { min: min.replace('.', ',') });
    }
    const reasonKey = DELIVERY_MESSAGE_KEYS[String(data.delivery[0])];
    if (reasonKey) return t(reasonKey);
  }
  // _create_payment_intent refuses when the restaurant has no provider keys.
  if (data?.payment_method) return t('checkout.errors.noPaymentConfigured');
  // The dine-in branch doesn't wrap a failed resource_api quote the way the
  // online branch does — a deactivated product comes back as
  // { quote: { detail: "Unknown or inactive product: <uuid>" } } with no uuid
  // list to name. Point at the cart rather than at nothing.
  if (data?.quote) return t('checkout.errors.productUnavailable');

  // DF-0573 — a field error used to end on « Échec de la commande, veuillez
  // réessayer »: the customer retried the same refused e-mail eight times.
  const contact = rejectedContactField(err);
  if (contact) return t(CONTACT_MESSAGE_KEYS[contact]);
  const fields = rejectedFieldKeys(err);
  if (fields.length > 0) {
    const fieldKey = fields.map((path) => FIELD_MESSAGE_KEYS[fieldRoot(path)]).find(Boolean);
    return t(fieldKey ?? 'checkout.errors.fieldTechnical');
  }

  if (payload.status === 429) return t('checkout.errors.tooManyRequests');

  return t('checkout.errors.generic');
}
